import React from "react";
import { Box, Flex, Heading, HStack } from "@chakra-ui/react";

/**
 * CountChip — the small neutral pill next to a panel title (e.g. "1 pool").
 * Template ref: `.count`.
 */
export const CountChip = ({ children, ...rest }) => (
  <Box
    as="span"
    flexShrink={0}
    fontSize="11px"
    fontWeight="800"
    color="portal.muted"
    bg="portal.soft"
    borderWidth="1px"
    borderColor="portal.line"
    px="7px"
    py="4px"
    borderRadius="999px"
    whiteSpace="nowrap"
    {...rest}
  >
    {children}
  </Box>
);

/**
 * Panel — the titled white card every list/detail surface sits in. Four optional
 * regions stacked vertically: title bar (title + count chip + actions), filter
 * row, body (scrolls), and footer (e.g. pagination).
 * Template ref: `.panel`, `.panelTitle`, `.filters`, `.foot`.
 *
 * Props:
 *   title    - panel heading text/node
 *   count    - value for the CountChip (number or node; omit to hide)
 *   actions  - right-aligned node(s) in the title bar (e.g. a primary button)
 *   filters  - node(s) for the filter row (omit to hide the row)
 *   footer   - node rendered below the body (e.g. <TablePagination/>)
 *   bodyProps- extra props spread onto the scrolling body Box
 *   children - panel body content
 */
const Panel = ({
  title,
  count,
  actions,
  filters,
  footer,
  bodyProps,
  children,
  ...rest
}) => (
  <Flex
    direction="column"
    minH={0}
    bg="portal.panel"
    borderWidth="1px"
    borderColor="portal.line"
    borderRadius="10px"
    boxShadow="panel"
    overflow="hidden"
    {...rest}
  >
    {(title || count != null || actions) && (
      <Flex
        align="center"
        justify="space-between"
        gap="8px"
        minH="44px"
        px="12px"
        py="8px"
        borderBottomWidth="1px"
        borderColor="portal.line"
      >
        {title ? (
          <Heading
            as="h2"
            fontSize="14px"
            letterSpacing="-0.02em"
            color="portal.text"
            isTruncated
            sx={{ marginBottom: "0 !important" }}
          >
            {title}
          </Heading>
        ) : (
          <Box />
        )}
        {(count != null || actions) && (
          <HStack spacing="8px" flexShrink={0}>
            {count != null && <CountChip>{count}</CountChip>}
            {actions}
          </HStack>
        )}
      </Flex>
    )}

    {filters && (
      <Flex
        align="center"
        gap="8px"
        flexWrap="wrap"
        px="12px"
        py="10px"
        bg="#FBFCFD"
        borderBottomWidth="1px"
        borderColor="portal.line"
      >
        {filters}
      </Flex>
    )}

    <Box flex="1" minH={0} overflow="auto" {...bodyProps}>
      {children}
    </Box>

    {footer}
  </Flex>
);

export default Panel;
